import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ROUTE } from "../../routes";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, FormikProps, Form } from "formik";
import { Input } from "../../components/Input";
import { TextArea } from "../../components/TextArea";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Box } from "../../components/Box";
import { IconButton } from "../../components/IconButton";
import { FaArrowLeft } from "react-icons/fa";
import { HiOutlineUser } from "react-icons/hi2";
import theme from "../../theme";
import { H1, H3 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { Image } from "../../components/Image";
import { Toggle } from "../../components/Toggle";
import { AlertBox } from "../../components/AlertBox";
import { ConfirmModal } from "../../components/Modals/ConfirmModal";
import { queryClient } from "../../queryClient";
import { useDeleteMandateType } from "../../data/useDeleteMandateType";
import { useUpdateMandateType } from "../../data/useUpdateMandateType";
import { useMandateType } from "../../data/useMandateType";
import { MandateType } from "../../types/MandateType";
import { useChecks } from "../../data/useChecks";
import * as icons from "../../assets/checkpoints";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { settingsNewApplicationTypeValidation } from "../../utils/validationSchema";
import { useDivisions } from "../../data/useDivisions";
import { ApplicationTypeDivisionFields } from "./ApplicationTypeDivisionFields";
import Email from "../../assets/Mail-Email-Letter.svg";
import deleteIcon from "../../assets/settings/delete-red.svg";
import editIcon from "../../assets/settings/edit.svg";
import RequestAdditionalDocument from "../../components/RequestAdditionalDocument";
import SettingsApplicationTypeForms from "./SettingsApplicationTypeForms";
import CustomTabs from "../../components/CustomTabs";
import { useDirtyForm } from "../../DirtyFormContext";

const Wrapper = styled(Box)`
  ${({ theme: { colors, radii, space } }) => `
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      text-decoration: none;
      color: inherit;
      background: ${colors.gray[10]};
      border-radius: ${radii[1]};
      padding: ${space[2]};
      position: relative;
      transition: all 0.2s ease 0s;
      border: 1px solid white;
  `}
`;

const EditTools = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: end;
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const EditToolsItem = styled.li`
  display: flex;
  align-items: center;
  font-weight: 400 !important;
  ${({ theme: { fontFamily, fontSizes, colors, space } }) => `
  font-family: ${fontFamily};
  font-size: ${fontSizes[2]};
  color: ${colors.blue[60]};
  padding-left: ${space[2]};
    `}
`;

const CheckPointList = styled.ul`
  list-style-type: none;
  margin-top: 0;
  padding: 0;
  min-height: 100px;
`;

const CheckPointListItem = styled.li`
  display: flex;
  align-items: center;
  line-height: 3.3em;
  font-weight: 400 !important;
  ${({ theme: { fontFamily, fontSizes, colors } }) => `
  font-family: ${fontFamily};
  font-size: ${fontSizes[2]};
  color: ${colors.blue[60]};
  border-bottom: 2px solid ${colors.gray[10]};
  `}
`;

/** Ids from a Division list that may come back nested or as bare ids. */
const unitIds = (units?: ({ id: number } | number)[] | null) =>
  (units ?? []).map((u) => (typeof u === "number" ? u : u.id));

/**
 * The type's assigned Divisions (Application Types aren't scoped to Teams).
 * Falls back to the single `primary_division` when the response has no list.
 */
const assignedIds = (mandateType: MandateType | null) => ({
  divisions: mandateType?.divisions
    ? unitIds(mandateType.divisions)
    : unitIds(mandateType?.primary_division ? [mandateType.primary_division] : []),
});

const SettingsApplicationTypeView = () => {
  const params = useParams();
  const navigate = useNavigate();

  const formRef = useRef<FormikProps<any>>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedMandateTypeId, setSelectedMandateTypeId] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [additionalDocumentArray, setAdditionalDocumentArray] = useState<
    number[]
  >([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<any[]>([]);

  const { mutate: deleteMandateType } = useDeleteMandateType();
  const { mutate: updateMandateType } = useUpdateMandateType();
  const { setFormDirty } = useDirtyForm();

  const {
    data: mandateTypeQuery,
    refetch,
    isLoading,
  } = useMandateType(parseInt(params.id));
  const [mandateType, setMandateType] = useState<MandateType | null>(null);
  const { data: checkListQuery } = useChecks();
  const checkList = checkListQuery?.data;

  // Names for the read-only Divisions row (the type may only carry ids).
  const { data: divisionsQuery } = useDivisions();
  const assigned = assignedIds(mandateType);
  const nameList = (ids: number[], units?: { id: number; display_name: string }[]) =>
    ids
      .map((id) => units?.find((u) => u.id === id)?.display_name ?? `#${id}`)
      .join(", ");

  useEffect(() => {
    const id = parseInt(params.id);
    setSelectedMandateTypeId(id);
  }, [params.id]);

  useEffect(() => {
    refetch();
  }, [editMode, refetch]);

  useEffect(() => {
    if (mandateTypeQuery?.data) {
      setMandateType(mandateTypeQuery.data);
      setAdditionalDocuments(mandateTypeQuery.data?.additional_document || []); // ← this line is key
    }
  }, [mandateTypeQuery?.data]);

  const handleUpdateMandateTypeEnabled = () => {
    updateMandateType(
      {
        params: {
          id: selectedMandateTypeId,
          is_disabled: !mandateType?.is_disabled as boolean,
        },
        id: selectedMandateTypeId,
      },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(
            ["mandateTypeQuery", selectedMandateTypeId],
            data
          );
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while updating the Application Type."
          );
        },
        onSettled: () => {
          refetch();
        },
      }
    );
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleDelete = () => {
    if (mandateType?.associated_individuals > 0) {
      setAlertMessage(
        `You can't delete this application type at this time because there are ${mandateType?.associated_individuals} individuals associated with it.`
      );
      setShowAlert(true);
      return;
    }
    setIsConfirmDeleteOpen(true);
  };

  const handleSubmit = (values: any) => {
    const currentCheckpoints =
      mandateTypeQuery?.data?.checkpoint?.map((cp) => cp.id) || [];
    const additionalDocumentCheck = checkList?.find(
      (check) => check.name === "additional_document_check"
    );

    if (!additionalDocumentCheck) {
      console.error("Additional Document Check is not found in the checklist.");
      return;
    }

    const additionalDocumentCheckId = additionalDocumentCheck.id;

    let newCheckpointArray = [...currentCheckpoints];

    if (additionalDocumentArray.length > 0) {
      // Add `additionalDocumentCheckId` if not already present
      if (!newCheckpointArray.includes(additionalDocumentCheckId)) {
        newCheckpointArray.push(additionalDocumentCheckId);
      }
    } else {
      // Remove `additionalDocumentCheckId` if present
      newCheckpointArray = newCheckpointArray.filter(
        (id) => id !== additionalDocumentCheckId
      );
    }

    updateMandateType(
      {
        params: {
          id: selectedMandateTypeId,
          name: values.name,
          description: values.description,
          action_reminder_email: values.action_reminder_email,
          divisions: values.divisions,
          checkpoint: newCheckpointArray,
          additional_document: additionalDocumentArray,
        },
        id: selectedMandateTypeId,
      },
      {
        onSuccess: (data) => {
          notify("Application Type has been saved", { type: "success" });
          queryClient.setQueryData(
            ["mandateTypeQuery", selectedMandateTypeId],
            data
          );
          setFormDirty(false);
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while updating the Application Type."
          );
        },
        onSettled: () => {
          setEditMode(false);
        },
      }
    );
  };

  const saveMandateType = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const confirmDeleteMandateType = () => {
    setIsConfirmDeleteOpen(false);
    deleteMandateType(
      { id: selectedMandateTypeId, deleted: true },
      {
        onSuccess: () => {
          notify("Application Type has been deleted", {
            type: "success",
          });
          navigate(ROUTE.SETTINGS_APPLICATION_TYPES);
          queryClient.invalidateQueries(["manadateTypes"]);
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while updating the Applicaiton Type."
          );
        },
        onSettled: () => {
          setSelectedMandateTypeId(null);
        },
      }
    );
  };

  if (!mandateType) {
    return null;
  }
  const handleAdditionalDocumentChange = (
    updatedArray: number[],
    fullDocs: any[]
  ) => {
    setAdditionalDocumentArray(updatedArray);
    setAdditionalDocuments(fullDocs); // store the latest full document list
  };

  const documentsContent = (
    <RequestAdditionalDocument
      onAdditionalDocumentChange={handleAdditionalDocumentChange}
    />
  );

  const formsContent = (
    <SettingsApplicationTypeForms
      initialFormsData={mandateType?._form}
      mandateTypeId={selectedMandateTypeId}
    />
  );

  const nonEditChecks =
    mandateType?.checkpoint && mandateType.checkpoint.length > 0 ? (
      <CheckPointList>
        {mandateType?.checkpoint.map((check) => (
          <CheckPointListItem key={check.id}>
            <Image
              src={icons[check.name.replace("_&", "")]}
              alt={`${check.actual_name} icon`}
              width="32px"
              height="30px"
              mr={1}
            />
            {check.actual_name}
          </CheckPointListItem>
        ))}
      </CheckPointList>
    ) : (
      <Paragraph>No checks added</Paragraph>
    );

  const nonEditForms =
    mandateType?._form && mandateType._form.length > 0 ? (
      <CheckPointList>
        {mandateType?._form.map((form) => (
          <CheckPointListItem key={form.id}>
            {form?._form.name}
          </CheckPointListItem>
        ))}
      </CheckPointList>
    ) : (
      <Paragraph>No forms added</Paragraph>
    );
  const nonEditDocuments =
    mandateType?.additional_document &&
    mandateType.additional_document.length > 0 ? (
      <CheckPointList>
        {mandateType.additional_document.map((doc) => (
          <CheckPointListItem key={doc.id}>{doc.name}</CheckPointListItem>
        ))}
      </CheckPointList>
    ) : (
      <Paragraph>No additional documents added</Paragraph>
    );

  return (
    <>
      <Flex
        bg="white"
        width="96%"
        gap="22"
        justifyContent="space-between"
        flexWrap="wrap"
        borderRadius="16px"
        padding={32}
      >
        <Flex width="100%" justifyContent="space-between">
          <Box>
            <Flex>
              {editMode ? (
                <IconButton
                  onClick={() => setEditMode(false)}
                  Type={FaArrowLeft}
                  right={2}
                  size="14px"
                  top={2}
                  color="gray.50"
                />
              ) : (
                <IconButton
                  onClick={() => navigate(ROUTE.SETTINGS_APPLICATION_TYPES)}
                  Type={FaArrowLeft}
                  right={2}
                  size="14px"
                  top={2}
                  color="gray.50"
                />
              )}
              {isLoading ? (
                <div>Loading...</div>
              ) : (
                <H1 fontSize={3} ml={2}>
                  {mandateType?.name}
                </H1>
              )}
            </Flex>
          </Box>
          <Box width="35%">
            <EditTools>
              {!editMode && (
                <>
                  <EditToolsItem>
                    <Flex flexDirection="row" alignItems="center">
                      <Toggle
                        mt={1}
                        isChecked={!mandateType?.is_disabled}
                        onChange={handleUpdateMandateTypeEnabled}
                      />
                      <Paragraph ml={1} color="gray.50">
                        Enabled
                      </Paragraph>
                    </Flex>
                  </EditToolsItem>
                  <EditToolsItem
                    onClick={handleEdit}
                    style={{ cursor: "pointer" }}
                  >
                    <Image
                      src={editIcon}
                      alt="edit icon"
                      height="25px"
                      width="25px"
                      marginRight={"4px"}
                    />
                    <Paragraph color="gray.50">Edit</Paragraph>
                  </EditToolsItem>
                  <EditToolsItem
                    onClick={handleDelete}
                    style={{ cursor: "pointer" }}
                  >
                    <Image
                      src={deleteIcon}
                      alt="delete icon"
                      height="25px"
                      width="25px"
                      marginRight={"4px"}
                    />
                    <Paragraph color="red.primary">Delete</Paragraph>
                  </EditToolsItem>
                </>
              )}
            </EditTools>
          </Box>
        </Flex>
        {showAlert && (
          <AlertBox closeAlert={() => setShowAlert(false)} alertType="warning">
            {alertMessage}
          </AlertBox>
        )}

        <Formik
          initialValues={{
            name: mandateType?.name,
            description: mandateType?.description,
            action_reminder_email: mandateType?.action_reminder_email,
            divisions: assigned.divisions,
          }}
          // Re-seed from the refetched type after a save, so the next Edit
          // starts from what was actually stored.
          enableReinitialize
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          validate={() => null}
          validationSchema={settingsNewApplicationTypeValidation}
          innerRef={formRef}
        >
          {(props) => (
            <Box width={"100%"}>
              <Form>
                {editMode ? (
                  <>
                    <H3 fontSize={2}>Details</H3>
                    <Box my={3}>
                      <H3 fontSize={1} fontWeight={500}>
                        Application Type Name
                      </H3>
                      <Input
                        name="name"
                        value={props.values.name}
                        onChange={props.handleChange}
                        isRequired
                        hasError={Boolean(props.errors.name)}
                        errorMessage={props.errors.name as string}
                      />
                    </Box>

                    <Box my={3}>
                      <H3 fontSize={1} fontWeight={500}>
                        Description
                      </H3>
                      <TextArea
                        name="description"
                        rows={4}
                        value={props.values.description}
                        onChange={props.handleChange}
                        isRequired
                        hasError={Boolean(props.errors.description)}
                        errorMessage={props.errors.description as string}
                      />
                    </Box>

                    <ApplicationTypeDivisionFields
                      isDisabled={props.isSubmitting}
                    />

                    <Flex justifyContent={"flex-end"} my={3}>
                      <EditToolsItem>
                        <Button
                          variant="primary"
                          onClick={saveMandateType}
                          isDisabled={!props.dirty || !props.isValid}
                        >
                          Save
                        </Button>
                      </EditToolsItem>
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex gap="22">
                      <Box flex={1.2}>
                        <Wrapper>
                          <Box mb={2}>
                            <H3 fontSize={2}>Application Type Name</H3>
                            <Paragraph my={0} fontSize={1} color="gray.50">
                              {isLoading ? (
                                <div>Loading...</div>
                              ) : (
                                mandateType?.name
                              )}
                            </Paragraph>
                          </Box>

                          <Box mb={2}>
                            <H3 fontSize={2}>Description</H3>
                            <Paragraph my={0} fontSize={1} color="gray.50">
                              {isLoading ? (
                                <div>Loading...</div>
                              ) : (
                                mandateType?.description
                              )}
                            </Paragraph>
                          </Box>

                          <Box mb={2}>
                            <H3 fontSize={2}>Divisions</H3>
                            <Paragraph my={0} fontSize={1} color="gray.50">
                              {nameList(assigned.divisions, divisionsQuery?.data) ||
                                "-"}
                            </Paragraph>
                          </Box>
                        </Wrapper>

                        <Flex
                          width={"100%"}
                          flexDirection="row"
                          alignItems="center"
                          justifyContent={"space-between"}
                          my={4}
                        >
                          <Flex>
                            <Image
                              src={Email}
                              alt="actions icon"
                              height="20px"
                              width="24px"
                            />
                            <Paragraph m={"0px"} fontWeight={600}>
                              Auto-email reminders
                            </Paragraph>
                          </Flex>

                          <Paragraph m={"0px"} fontWeight={600}>
                            {mandateType?.action_reminder_email
                              ? "Active"
                              : "Inactive"}
                          </Paragraph>
                        </Flex>
                      </Box>

                      <Box flex={0.8}>
                        <Wrapper>
                          <Paragraph ml={2}>
                            <HiOutlineUser size={36} color={theme.colors.gray[60]} />
                          </Paragraph>
                          <Flex justifyContent="start">
                            <Paragraph ml={2} width="50%">
                              {mandateType?.associated_individuals}
                              {mandateType?.associated_individuals === 1
                                ? " Individual is"
                                : " Individuals are"}{" "}
                              associated with this application type
                            </Paragraph>
                          </Flex>
                        </Wrapper>
                      </Box>
                    </Flex>
                  </>
                )}

                {editMode && (
                  <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent={"space-between"}
                    borderBottom={"1px solid"}
                    borderBottomColor="gray.100"
                    pb={3}
                  >
                    <Flex>
                      <Image
                        src={Email}
                        alt="actions icon"
                        height="20px"
                        width="24px"
                      />
                      <Paragraph m={"0px"} fontWeight={600}>
                        Auto-email reminders
                      </Paragraph>
                    </Flex>

                    <Toggle
                      mt={1}
                      isChecked={props.values.action_reminder_email}
                      onChange={(e: any) => {
                        props.setFieldValue(
                          "action_reminder_email",
                          e.target.checked
                        );
                        saveMandateType()
                        
                      }}
                    />
                  </Flex>
                )}
              </Form>
            </Box>
          )}
        </Formik>

        {/* <Box>
          {mandateType?.checkpoint.length === 0 ? (
            <Flex justifyContent="center" alignItems="center" minHeight="25vh">
              <Loader />
            </Flex>
          ) : (
            <>
              {editMode && (
                <Box mb={4} mr={4} pr={2}>
                  <AlertBox alertType="info">
                    Checks can’t be changed once application type is created.
                  </AlertBox>
                </Box>
              )}
            </>
          )}
        </Box> */}

        {editMode ? (
          <CustomTabs
            tabs={[
              {
                label: (
                  <>
                    Checks{"  "}
                    <span style={{ color: "gray" }}>
                      {mandateType?.checkpoint?.length || 0}
                    </span>
                  </>
                ),
                content: nonEditChecks,
              },
              {
                label: (
                  <>
                    Forms{"  "}
                    <span style={{ color: "gray" }}>
                      {mandateType?._form?.length || 0}
                    </span>
                  </>
                ),
                content: formsContent,
              },
              {
                label: (
                  <>
                    Additional Documents{"  "}
                    <span style={{ color: "gray" }}>
                      {additionalDocuments.length}
                    </span>
                  </>
                ),
                content: documentsContent,
              },
            ]}
          />
        ) : (
          <CustomTabs
            tabs={[
              {
                label: `Checks ${mandateType?.checkpoint?.length || 0}`,
                content: nonEditChecks,
              },
              {
                label: `Forms ${mandateType?._form?.length || 0}`,
                content: nonEditForms,
              },
              {
                label: `Additional Documents ${additionalDocuments.length}`,
                content: nonEditDocuments,
              },
            ]}
          />
        )}
      </Flex>

      <ConfirmModal
        isOpen={isConfirmDeleteOpen}
        onRequestClose={() => {
          setIsConfirmDeleteOpen(false);
        }}
        handleConfirm={confirmDeleteMandateType}
        title="Are you sure you want to delete this application type?"
        content="This action cannot be reversed."
      />
    </>
  );
};

export default SettingsApplicationTypeView;
